<script lang="ts">
	import {
		Button,
		CardGrid,
		Container,
		Label,
		LinkWithIcon,
		Popover,
		SelectCard,
		Text,
	} from '@Shelly/shelly-design-system/index';
	import { navigateToShop } from 'src/components/Lists/list-helpers';
	import PromoCampaignCard from 'src/components/Promotions/molecules/PromoCampaignCard.svelte';
	import ThirtyMillionDevicesModal from 'src/components/Modals/ThirtyMillionDevicesModal.svelte';
	import XlX2FlashDealModal from 'src/components/Modals/XlX2FlashDealModal.svelte';
	import WallDisplayFlashSale2Modal from 'src/components/Modals/WallDisplayFlashSale2Modal.svelte';
	import SpringDeals2026Modal from 'src/components/Modals/SpringDeals2026Modal.svelte';
	import SmartHomeChallenge2026Modal from 'src/components/Modals/SmartHomeChallenge2026Modal.svelte';
	import { PROMOTIONS } from 'src/constants';
	import { copy } from 'src/helpers/copy_text';
	import { dateFormat } from 'src/helpers/date_helper';
	import { save_home } from 'src/helpers/home_helpers';
	import { getPromoExpiryText } from 'src/helpers/promo_helpers';
	import { canManageHomeEntities } from 'src/helpers/houses/homes_helpers';
	import { home_data, user_data, user_settings } from 'src/stores/account/user';
	import { devices } from 'src/stores/lists/devices';
	import { homes } from 'src/stores/account/home';
	import type { DiscountType } from 'src/types/DiscountType';
	import { onMount } from 'svelte';
	import { format, locale } from 'svelte-i18n';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	interface DiscountExtendedType extends DiscountType {
		details?: {
			devices?: {
				[key: string]: string;
			};
		};
		type: {
			color: any;
			abbr: string;
			icon: string;
			title: string;
			translation: string;
		};
	}

	let showGen1ReplacementPopover = false;
	let showThirtyMillionModal = false;
	let showXlX2FlashDealModal = false;
	let showWallDisplayFlashSale2Modal = false;
	let showSpringDeals2026Modal = false;
	let showSmartHomeChallenge2026Modal = false;

	const now = Date.now();
	const isThirtyMillionActive = now >= PROMOTIONS.THIRTY_MILLION_DEVICES.START_DATE;
	$: isXlX2FlashDealActive =
		(!!$home_data?.aep?.xlx2?.ts || import.meta.env.DEV) &&
		now >= PROMOTIONS.XL_X2_FLASH_DEAL.START_DATE &&
		now < PROMOTIONS.XL_X2_FLASH_DEAL.END_DATE;
	$: isMainUnitAccount = canManageHomeEntities($homes.list);
	$: isWallDisplayFlashSale2Active =
		(!!$home_data?.aep?.wdfs2?.code || import.meta.env.DEV) &&
		isMainUnitAccount &&
		now >= PROMOTIONS.WALL_DISPLAY_FLASH_SALE_2.START_DATE &&
		now < PROMOTIONS.WALL_DISPLAY_FLASH_SALE_2.END_DATE;
	const isSpringDeals2026Active = now >= PROMOTIONS.SPRING_DEALS_2026.START_DATE;
	const isSmartHomeChallenge2026Active = now >= PROMOTIONS.SMART_HOME_CHALLENGE_2026.START_DATE;

	$: thirtyMillionExpiryText = $format(
		getPromoExpiryText(PROMOTIONS.THIRTY_MILLION_DEVICES.END_DATE)?.key,
		getPromoExpiryText(PROMOTIONS.THIRTY_MILLION_DEVICES.END_DATE)?.object,
	);

	$: xlX2FlashDealExpiryText = $format(
		getPromoExpiryText(PROMOTIONS.XL_X2_FLASH_DEAL.END_DATE)?.key,
		getPromoExpiryText(PROMOTIONS.XL_X2_FLASH_DEAL.END_DATE)?.object,
	);

	$: wallDisplayFlashSale2ExpiryText = $format(
		getPromoExpiryText(PROMOTIONS.WALL_DISPLAY_FLASH_SALE_2.END_DATE)?.key,
		getPromoExpiryText(PROMOTIONS.WALL_DISPLAY_FLASH_SALE_2.END_DATE)?.object,
	);

	$: springDeals2026ExpiryText = $format(
		getPromoExpiryText(PROMOTIONS.SPRING_DEALS_2026.END_DATE)?.key,
		getPromoExpiryText(PROMOTIONS.SPRING_DEALS_2026.END_DATE)?.object,
	);

	$: smartHomeChallenge2026ExpiryText = $format(
		getPromoExpiryText(PROMOTIONS.SMART_HOME_CHALLENGE_2026.END_DATE)?.key,
		getPromoExpiryText(PROMOTIONS.SMART_HOME_CHALLENGE_2026.END_DATE)?.object,
	);

	$: thirtyMillionImage = $user_data?.timezone.startsWith('America/')
		? './images/30m_us.png'
		: `./images/30m_${['de', 'fr', 'it'].includes($locale) ? $locale : 'default'}.png`;

	$: xlX2FlashDealImage = './images/xl_x2_flash_deal_default_99.png';

	$: wallDisplayFlashSale2Image = $user_data?.timezone.startsWith('Europe/London')
		? './images/wall_display_flash__deal_pound.png'
		: './images/wall_display_flash__deal_default.png';

	$: springDeals2026Image = $user_data?.timezone?.startsWith('America/')
		? './images/shelly_spring_deals_2026_App_default_us.png'
		: './images/shelly_spring_deals_2026_App_default.png';

	$: smartHomeChallenge2026Image = $user_data?.timezone?.startsWith('America/')
		? './images/ioT-Challenge-us.png'
		: `./images/ioT-Challenge-${['de', 'fr', 'it'].includes($locale) ? $locale : 'default'}.png`;

	/**
	 * Build the promotions list, show active campaigns before expired ones,
	 * and within each group sort by newest start date first.
	 */
	$: promotions = (
		[
			isThirtyMillionActive && {
				id: 'thirtyMillion',
				startDate: PROMOTIONS.THIRTY_MILLION_DEVICES.START_DATE,
				image: thirtyMillionImage,
				title: $format('title_celebrating_30_million_devices', {
					default: 'Celebrating 30 MILLION devices sold',
				}),
				description: $format('text_30_million_intro', {
					default:
						"To celebrate 30 million Shelly devices powering the world, we're scaling our rewards to match your ambition. The more you automate, the more you save.",
				}),
				expiryText: thirtyMillionExpiryText || $format('label_expired', { default: 'Expired' }),
				showActiveBadge: !!thirtyMillionExpiryText,
				onLearnMore: () => {
					showThirtyMillionModal = true;
					const thm = { ...$home_data?.aep?.thm };
					thm.seen = new Date().getTime();
					thm.clickOnPromotionButton = (thm?.clickOnPromotionButton || 0) + 1;
					save_home('aep', { ...$home_data.aep, thm });
				},
			},
			isXlX2FlashDealActive && {
				id: 'xlX2FlashDeal',
				startDate: PROMOTIONS.XL_X2_FLASH_DEAL.START_DATE,
				image: xlX2FlashDealImage,
				title: $format('title_wall_display_flash_sale', {
					default: 'WallDisplay Flash Sale',
				}),
				description: $format('text_wall_display_flash_sale_message', {
					default: 'Your smart home deserves a control center.',
				}),
				expiryText: xlX2FlashDealExpiryText || $format('label_expired', { default: 'Expired' }),
				showActiveBadge: !!xlX2FlashDealExpiryText,
				onLearnMore: () => {
					showXlX2FlashDealModal = true;
					const xlx2 = { ...$home_data?.aep?.xlx2 };
					xlx2.seen = new Date().getTime();
					xlx2.clickOnPromotionButton = (xlx2?.clickOnPromotionButton || 0) + 1;
					save_home('aep', { ...$home_data.aep, xlx2 });
				},
			},
			isWallDisplayFlashSale2Active && {
				id: 'wallDisplayFlashSale2',
				startDate: PROMOTIONS.WALL_DISPLAY_FLASH_SALE_2.START_DATE,
				image: wallDisplayFlashSale2Image,
				title: $format('title_wall_display_flash_sale_2', {
					default: 'Wall Display Flash Sale',
				}),
				description: $format('text_wall_display_flash_sale_2_message', {
					default: 'An Exclusive Offer for Our Most Loyal Users',
				}),
				expiryText:
					wallDisplayFlashSale2ExpiryText || $format('label_expired', { default: 'Expired' }),
				showActiveBadge: !!wallDisplayFlashSale2ExpiryText,
				onLearnMore: () => {
					showWallDisplayFlashSale2Modal = true;
					const wdfs2 = { ...$home_data?.aep?.wdfs2 };
					wdfs2.seen = new Date().getTime();
					wdfs2.clickOnPromotionButton = (wdfs2?.clickOnPromotionButton || 0) + 1;
					save_home('aep', { ...$home_data.aep, wdfs2 });
				},
			},
			isSpringDeals2026Active && {
				id: 'springDeals2026',
				startDate: PROMOTIONS.SPRING_DEALS_2026.START_DATE,
				image: springDeals2026Image,
				title: $format('title_spring_deals', {
					default: 'Spring Deals',
				}),
				description: $format('text_spring_deals_description', {
					default:
						'Bring your home to the next level with smart solutions designed for comfort and savings - now at our best prices this season.',
				}),
				expiryText:
					springDeals2026ExpiryText || $format('label_expired', { default: 'Expired' }),
				showActiveBadge: !!springDeals2026ExpiryText,
				onLearnMore: () => {
					showSpringDeals2026Modal = true;
					const sd = { ...$home_data?.aep?.sd };
					sd.seen = new Date().getTime();
					sd.clickOnPromotionButton = (sd?.clickOnPromotionButton || 0) + 1;
					save_home('aep', { ...$home_data.aep, sd });
				},
			},
			isSmartHomeChallenge2026Active && {
				id: 'smartHomeChallenge2026',
				startDate: PROMOTIONS.SMART_HOME_CHALLENGE_2026.START_DATE,
				icon: 'fas fa-trophy-star',
				image: smartHomeChallenge2026Image,
				title: $format('title_smart_home_challenge_2026', {
					default: 'Smart Home Challenge 2026',
				}),
				description: $format('text_smart_home_challenge_2026_card_description', {
					default:
						"Show the community what you've built with Shelly. Win up to 500€ in store credit — and get your project in front of thousands of smart home enthusiasts worldwide.",
				}),
				expiryText:
					smartHomeChallenge2026ExpiryText || $format('label_expired', { default: 'Expired' }),
				showActiveBadge: !!smartHomeChallenge2026ExpiryText,
				onLearnMore: () => {
					showSmartHomeChallenge2026Modal = true;
					const shc26 = { ...$home_data?.aep?.shc26 };
					shc26.seen = new Date().getTime();
					shc26.clickOnPromotionButton = (shc26?.clickOnPromotionButton || 0) + 1;
					save_home('aep', { ...$home_data.aep, shc26 });
				},
			},
		].filter(Boolean) as Array<{
			id: string;
			startDate: number;
			image: string;
			title: string;
			description: string;
			expiryText: string;
			showActiveBadge: boolean;
			onLearnMore: () => void;
			icon?: string;
		}>
	).sort((a, b) => {
		const activeDiff = Number(b.showActiveBadge) - Number(a.showActiveBadge);
		if (activeDiff !== 0) return activeDiff;
		return b.startDate - a.startDate;
	});

	onMount(() => {
		const now = Math.floor(Date.now() / 1000);
		const clonedUserCampaigns = $user_settings?.aep;
		if (clonedUserCampaigns) {
			Object.values(clonedUserCampaigns).forEach((campaign) => {
				Object.values(campaign).forEach((discount) => {
					if (!discount.hasOwnProperty('seen')) {
						discount.seen = now;
					}
				});
			});
			user_settings.set({ ...$user_settings, aep: clonedUserCampaigns });
		}
		const clonedG1Replacement = $home_data?.aep?.g1d;
		if (clonedG1Replacement) {
			Object.values(clonedG1Replacement).forEach((discount: DiscountType) => {
				if (!discount.hasOwnProperty('seen')) {
					discount.seen = now;
				}
			});
			save_home('aep', { ...$home_data.aep, g1d: clonedG1Replacement });
		}
	});

	$: newDevices = $user_settings?.aep?.nd
		? Object.entries($user_settings?.aep?.nd).map(([key, promo]: [string, DiscountType]) => ({
				...promo,
				type: {
					abbr: 'nd',
					color: 'primary',
					icon: 'fas fa-mobile-screen-button',
					title: $format('discount_title_added_devices', {
						default: `Added ${parseInt(key, 10)} devices`,
						values: {
							count: parseInt(key, 10),
						},
					}),
					translation: `${$format('discount_label_added_devices', { default: 'added devices' })} - ${parseInt(key, 10)}`,
				},
			}))
		: [];

	$: earthDay =
		$user_settings?.aep?.ed &&
		now >= PROMOTIONS.EARTH_DAY.START_DATE &&
		now < PROMOTIONS.EARTH_DAY.END_DATE
			? Object.values($user_settings?.aep?.ed).map((promo: DiscountType) => ({
					...promo,
					type: {
						abbr: 'ed',
						color: 'success',
						icon: 'fas fa-earth-americas',
						title: $format('discount_title_earth_day', {
							default: 'One time-22% OFF',
						}),
						translation: $format('discount_label_earth_day', {
							default: 'Earth Day',
						}),
					},
				}))
			: [];

	// Gen1 replacement: backend may provide $home_data?.aep?.g1d e.g. { b1: { code, expiry, ts } }

	$: gen1Replacement =
		$home_data?.aep?.g1d &&
		now >= PROMOTIONS.GEN_1_REPLACEMENT.START_DATE &&
		now < PROMOTIONS.GEN_1_REPLACEMENT.END_DATE
			? (Object.values($home_data.aep.g1d)
					.filter(
						(item): item is DiscountExtendedType =>
							item != null && typeof item === 'object' && 'code' in item,
					)
					.map((promo: DiscountExtendedType) => ({
						...promo,
						type: {
							abbr: 'g1d',
							color: 'primary',
							icon: 'fas fa-percent',
							title: $format('new_discount_title_g1_replacement', {
								default:
									'50% OFF for {count} devices Shelly Gen 3, which can be any of Shelly 1, Shelly 1PM, or Shelly 2PM',
								values: {
									count:
										promo?.details?.devices !== undefined
											? Object.values(promo?.details?.devices).length > 15
												? 15
												: Object.values(promo?.details?.devices).length
											: 0,
								},
							}),
							translation: $format('new_title_gen1_replacement', {
								default: 'Gen1 Upgrade Programme',
							}),
						},
					})) as DiscountExtendedType[])
			: [];

	$: discounts = [...newDevices, ...earthDay, ...gen1Replacement].sort(
		(a: DiscountType, b: DiscountType) => b.ts - a.ts,
	) as DiscountExtendedType[];

	// Utility function to parse expiry field to a Date object
	function parseExpiryDate(expiry: number | string | undefined): Date | undefined {
		if (!expiry) return undefined;
		if (typeof expiry === 'number') {
			// If it's a 10-digit number, treat as seconds; if 13, as ms
			return new Date(expiry > 1e12 ? expiry : expiry * 1000);
		} else if (typeof expiry === 'string') {
			// Accepts YYYY-MM-DD or ISO string
			const date = new Date(expiry);
			return isNaN(date as any) ? undefined : date;
		}
		return undefined;
	}

	// Utility function to check if a discount is expired
	function isDiscountExpired(expiry: number | string | undefined): boolean {
		const expiryDate = parseExpiryDate(expiry);
		return expiryDate ? expiryDate.getTime() < Date.now() : false;
	}
</script>

<Container class="mb-s">
	<Text styleType="h2" class="mb-s">
		{$format('text_unlock_exclusive_savings', {
			default: 'Unlock exclusive savings!',
		})}
	</Text>

	<Text styleType="body-small">
		{$format('text_here_discount_codes', {
			default:
				'Here are the discount codes that you can use to save money on your next purchases.',
		})}
	</Text>

	{#if promotions.length > 0}
		<hr class="mt-m mb-m" />

		<Text styleType="h2" class="mb-m">
			{$format('heading_promotions', { default: 'Promotions' })}
		</Text>

		<CardGrid sm={300} noPaddingBottom>
			{#each promotions as promotion (promotion.id)}
				<PromoCampaignCard
					image={promotion.image}
					alt={promotion.title}
					title={promotion.title}
					cardIcon={promotion.icon}
					description={promotion.description}
					expiryText={promotion.expiryText}
					showActiveBadge={promotion.showActiveBadge}
					activeBadgeLabel={$format('label_active', { default: 'Active' })}
					learnMoreLabel={$format('link_learn_more', { default: 'Learn more' })}
					on:learnMore={promotion.onLearnMore}
				/>
			{/each}
		</CardGrid>

		<ThirtyMillionDevicesModal bind:showModal={showThirtyMillionModal} />
		<XlX2FlashDealModal bind:showModal={showXlX2FlashDealModal} />
		<WallDisplayFlashSale2Modal bind:showModal={showWallDisplayFlashSale2Modal} />
		<SpringDeals2026Modal bind:showModal={showSpringDeals2026Modal} />
		<SmartHomeChallenge2026Modal bind:showModal={showSmartHomeChallenge2026Modal} />
	{/if}

	{#if isMainUnitAccount}
		<hr class="mt-m mb-m" />

		{#if discounts.length > 0}
			<Text styleType="h2" class="mb-m"
				>{$format('active_discount_codes', {
					default: 'Active discount codes',
				})}</Text
			>
		{/if}

		{#if discounts.length <= 0}
			<Text styleType="body-small" class="mb-s">
				{$format('text_no_active_discount_codes', {
					default: 'There are currently no active discount codes.',
				})}
			</Text>
		{/if}

		<CardGrid sm={200} noPaddingBottom>
			{#each discounts as discount}
				<SelectCard
					cardBgColor={$showNewLayout ? 'color-1' : undefined}
					class="width-auto"
					type="vertical"
					disabled={isDiscountExpired(discount.expiry)}
					title={discount.type?.title}
					descriptiveText={`${$format('received_on', { default: 'Received on' })} ${dateFormat(new Date(discount.ts * 1000), 'Y-m-d H:i:s')}` +
						(discount.expiry
							? (() => {
									const expiryDate = parseExpiryDate(discount.expiry);
									if (expiryDate) {
										const isExpired = isDiscountExpired(discount.expiry);
										const labelKey = isExpired ? 'expired_on' : 'expires_on';
										const defaultLabel = isExpired
											? $format('label_expired_one', { default: 'Expired on' })
											: $format('label_expires_one', { default: 'Expires on' });
										return `<br>${$format(labelKey, { default: defaultLabel })} ${dateFormat(expiryDate, 'Y-m-d')}`;
									}
									return '';
								})()
							: '')}
					icon={discount.type.icon}
				>
					<Label
						slot="leftBadge"
						size="s"
						value={discount.type.translation}
						type={discount.type.color}
						noRadiusType="second"
					/>

					<svelte:fragment slot="secondLeftBadge">
						{#if discount.type.abbr.toLowerCase() === 'g1d'}
							<Popover
								hideCloseBtn
								placement="top"
								show={showGen1ReplacementPopover}
								on:close={() => (showGen1ReplacementPopover = false)}
							>
								<Label
									size="s"
									icon="fas fa-info-circle"
									iconColor="--color-primary"
									noRadiusType="third"
									on:click={() =>
										(showGen1ReplacementPopover = !showGen1ReplacementPopover)}
									class="cursor-pointer"
								/>
								<div slot="customContent">
									<Text styleType="body-extra-small">
										{$format('new_popover_text_gen1_upgrade_devices', {
											default:
												'These are the devices from Gen 1 that are eligible for the upgrade:',
										})}
										<ul class="pl-l">
											{#each discount?.details?.devices !== undefined ? Object.values(discount?.details?.devices) : [] as item}
												<li>
													{$devices[item].name}
												</li>
											{/each}
										</ul>
									</Text>
									<Text styleType="body-extra-small">
										{@html $format('new_text_gen1_replacement_7', {
											default:
												'Please see the <a class="text-primary body-extra-small-styles text-decoration-none" href="https://www.shelly.com/pages/gen1-replacement-info" target="_blank">FAQ</a> for terms and conditions.',
										})}
									</Text>
								</div>
							</Popover>
						{/if}
					</svelte:fragment>

					<svelte:fragment slot="customAction">
						{#if !isDiscountExpired(discount.expiry)}
							<LinkWithIcon
								iconInRight
								on:click={() =>
									copy(
										discount.code,
										$format('discount_code_copied_successfully', {
											default: 'Discount code copied successfully!',
										}),
									)}
								size="m"
								icon="fa-light fa-copy"
								text={discount.code}
							/>
						{:else}
							<LinkWithIcon
								iconInRight
								size="m"
								icon="fa-light fa-copy"
								text={$format('copy_discount_code', {
									default: 'Copy discount code',
								})}
								disabled={true}
								style="opacity: 0.5; text-decoration: line-through;"
							/>
						{/if}
					</svelte:fragment>
				</SelectCard>
			{/each}
		</CardGrid>

		{#if discounts.length > 0}
			<div class="text-center">
				<Button
					class="mt-l"
					on:click={() => navigateToShop('https://www.shelly.com/collections/all-products')}
				>
					{$format('button_shop_now', { default: 'Shop Now' })}
				</Button>
			</div>
		{/if}
	{/if}
</Container>
